<template>
  <div class="max-w-md mx-auto p-4">
    <!-- Add device detector -->
    <div ref="deviceDetector" class="hidden sm:block md:hidden lg:hidden xl:hidden"></div>
    
    <h2 class="text-2xl font-bold mb-6 text-center">{{ $t('changePhone') }}</h2>
    
    <!-- Current Phone Display -->
    <div class="mb-6 text-center">
      <p class="text-gray-600">{{ $t('currentPhone') }}: {{ currentPhone }}</p>
    </div>

    <!-- Phone Input Form -->
    <form v-if="!isOtpMode" @submit.prevent="handleSubmit" class="space-y-4">
      <div class="relative">

        <input
          v-if="!isMobile"
          v-model="newPhoneWithoutPrefix"
          type="text"
          inputmode="text"
          @input="onPhoneInput"
          @keydown="onPhoneKeyDown"
          :placeholder="t('newPhoneNumber')"
          class="w-full h-14 pl-3 pr-3 border border-[#e6e6e6] rounded-3xl text-left ltr"
        />
        <!-- Mobile Input (hidden on desktop) -->
        <input
          v-else
          v-model="newPhoneWithoutPrefix"
          type="number"
          inputmode="numeric"
          pattern="[0-9]*"
          @input="onPhoneInput"
          @keydown="onPhoneKeyDown"
          :placeholder="t('newPhoneNumber')"
          class="w-full h-14 pl-3 pr-3 border border-[#e6e6e6] rounded-3xl text-left ltr"
        />
      </div>

      <button 
        type="submit"
        class="w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
        :disabled="loading || !isValidPhone"
      >
        <span v-if="loading" class="spinner-loading"></span>
        <span v-else>{{ $t('sendOtp') }}</span>
      </button>
    </form>

    <!-- OTP Verification Form -->
    <div v-else class="space-y-4">
      <otp-input
        v-model="otpCode"
        :resend-delay="settings.settings?.resend_delay || 60"
        @resend="handleResendOtp"
        :otp-length="settings.settings?.otp_length || 4"
        @complete="verifyOtp"
        ref="otpInputRef"
      />
      
      <div class="flex gap-4">
        <button
          @click="handleBack"
          class="flex-1 flex justify-center items-center py-2 px-4 border border-main text-sm font-medium rounded-[32px] h-14 text-black bg-white focus:outline-none focus:ring-0"
        >
          {{ $t('back') }}
        </button>
        <button
          @click="verifyOtp"
          :disabled="loading"
          class="flex-1 flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
        >
          <span v-if="loading" class="spinner-loading"></span>
          <span v-else>{{ $t('verify') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import OtpInput from './OtpInput.vue'
import { showSuccess, showError } from '../../utils/swal'
import { t } from '../../utils/translate'
import { convertToEnglishNumbers, isPersianOrArabicNumber } from '../../utils/number'

const smsir = window.sms_ir_login || {}
const $ = window.jQuery

const currentPhone = ref(smsir.currentPhone || '')
const newPhoneWithoutPrefix = ref('')
const newPhone = ref('')
const isValidPhone = ref(false)
const loading = ref(false)
const isOtpMode = ref(false)
const otpCode = ref('')

const settings = ref({})
const otpInputRef = ref(null)

// Add device detection logic
const deviceDetector = ref(null)
const isMobile = ref(false)

onMounted(() => {
  // Check if mobile on initial load
  checkDeviceType()
  // Add resize listener to detect device changes
  window.addEventListener('resize', checkDeviceType)
  fetchSettings()
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDeviceType)
})

const checkDeviceType = () => {
  // Use a more reliable approach to detect mobile devices
  if (typeof window !== 'undefined') {
    // Use window.innerWidth as the primary detection method
    isMobile.value = window.innerWidth < 768
    
    // Additional check for mobile user agents as a fallback
    if (!isMobile.value) {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      isMobile.value = /android|webos|iphone|ipad|ipod|blackberry|IEMobile|Opera Mini/i.test(userAgent);
    }
  } else {
    // Default to desktop if window is not available
    isMobile.value = false;
  }
}

// Force initial value to false for desktop
isMobile.value = false;

watch(newPhoneWithoutPrefix, (newValue) => {
  if (newValue) {
    // For validation, ensure it starts with 9
    const validationValue = newValue.startsWith('0') ? newValue.substring(1) : newValue
    
    // Update the full phone number with prefix
    newPhone.value = `${newValue}`
    
    // Validate phone number (must be 10 digits for Iranian mobile starting with 9)
    isValidPhone.value = (newValue.length === 11 && newValue.startsWith('9') || 
                         newValue.length === 10 && newValue.startsWith('9')) && 
                         `${newValue}` !== currentPhone.value
  } else {
    newPhone.value = ''
    isValidPhone.value = false
  }
}, { immediate: true })

const onPhoneInput = (event) => {
  let value = event.target.value
  
  // Convert Persian/Arabic numbers to English
  value = convertToEnglishNumbers(value)
  
  // Keep only digits
  value = value.replace(/\D/g, '')
  
  // Handle the first digit - must be 9
  if (value.length > 0) {
    // First character must be 9
    if (value[0] !== '9') {
      // If first digit is wrong - replace value with either:
      // 1. Empty string if no valid prefix exists
      // 2. Previous valid value starting with 9
      if (newPhoneWithoutPrefix.value && newPhoneWithoutPrefix.value.startsWith('9')) {
        // Keep the previous valid value
        event.preventDefault()
        return
      } else {
        // If the input was 0, try to interpret as starting with 09
        if (value.startsWith('0') && value.length > 1) {
          // Remove the leading 0 and check if the next digit is 9
          value = value.substring(1)
          if (!value.startsWith('9')) {
            // If still doesn't start with 9, prevent
            newPhoneWithoutPrefix.value = ''
            showError(t('phoneStartWith9'))
            return
          }
        } else {
          // For any other first digit, prevent and show error
          newPhoneWithoutPrefix.value = ''
          showError(t('phoneStartWith9'))
          return
        }
      }
    }
  }
  
  // Enforce max length
  if (value.length > 10) {
    value = value.slice(0, 10)
  }
  
  // Update input value
  newPhoneWithoutPrefix.value = value
}

// Add keydown event handler to prevent non-9 first digit
const onPhoneKeyDown = (event) => {
  const input = event.target
  const value = input.value
  
  // If input is empty and key pressed is not 9 or 0 (for 09...)
  if (value.length === 0 && !/[90]/.test(event.key) && /^\d$/.test(event.key)) {
    event.preventDefault()
    showError(t('phoneStartWith9'))
  }
}

const handleSubmit = async () => {
  try {
    if (!newPhone.value) {
      showError(t('pleaseEnterValidPhone'))
      return
    }

    if (!isValidPhone.value) {
      showError(t('pleaseEnterValidPhone'))
      return
    }

    loading.value = true

    const data = {
      action: 'smsir_request_change_phone_otp',
      phone: newPhone.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      isOtpMode.value = true
      showSuccess(response.data.message || t('otpSent'))
    } else {
      showError(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const verifyOtp = async () => {
  try {
    loading.value = true

    const data = {
      action: 'smsir_verify_change_phone',
      phone: newPhone.value,
      otp: otpCode.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      showSuccess(response.data.message || t('phoneChangeSuccess'))
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      throw new Error(response.data?.message || t('invalidOtp'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async () => {
  try {
    loading.value = true
    const data = {
      action: 'smsir_request_change_phone_otp',
      phone: newPhone.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      showSuccess(response.data.message || t('otpResent'))
      if (otpInputRef.value) {
        otpInputRef.value.startCountdown()
      }
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const handleBack = () => {
  isOtpMode.value = false
  otpCode.value = ''
}

const fetchSettings = async () => {
  try {
    const response = await $.post(smsir.ajaxUrl, {
      action: 'smsir_get_settings',
      nonce: smsir.nonce 
    })
    if (response.success) {
      settings.value = response.data
    } else {
      console.error('Failed to load settings:', response.data?.message)
    }
  } catch (err) {
    console.error('Error fetching settings:', err)
  }
}
</script>

<style scoped>
.ltr {
  direction: ltr;
}

input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="text"][type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>